/**
 * Per-conversation viewer-presence tracker (M5.7) — widget side.
 *
 * Svelte 5 port of admin-console's PresenceTracker. Same protocol, same
 * semantics: sends `viewing.start` / `viewing.stop` frames as the operator
 * opens/closes a thread, exposes peer-viewer list (self filtered) for the
 * thread header pill.
 *
 * State is rune-backed (`$state`) so consumers like ConversationHeader can
 * read `presence.peers` reactively and the badge re-renders on every
 * `conversation.viewing.updated` broadcast.
 *
 * Wired from App.svelte:
 *   - construct after realtime is connected,
 *   - set as the module singleton via setPresence so ConversationHeader can
 *     find it without prop-drilling,
 *   - call setActive on route change and visibilitychange,
 *   - route `conversation.viewing.updated` frames into handleViewingUpdated,
 *   - register resumeActive on realtime.onOpen,
 *   - dispose on App teardown.
 */

export interface PresenceViewer {
  userId: string;
  displayName: string;
}

interface ViewingUpdatedPayload {
  conversationId: string;
  viewers: PresenceViewer[];
}

export class PresenceTracker {
  // Rune state — readable directly by consumers (presence.active /
  // presence.peers) and reactive in Svelte templates.
  active = $state<string | null>(null);
  peers = $state<PresenceViewer[]>([]);

  constructor(
    private readonly send: (frame: object) => void,
    private readonly selfId: () => string | null,
    private readonly tenantId: () => string | null = () => null,
  ) {}

  /**
   * Build a viewing frame. `tenantId` scopes the frame to the tenant this
   * operator is working in: realtime-gateway namespaces its viewer sets by
   * tenant so a socket cannot join (or read) another tenant's viewer list by
   * naming its conversationId. Omitted when unknown — the gateway then
   * resolves it from the ticket, which is unambiguous for the single-tenant
   * operators that are the norm.
   */
  private viewingFrame(type: 'viewing.start' | 'viewing.stop', convId: string): object {
    const tenantId = this.tenantId();
    return tenantId ? { type, conversationId: convId, tenantId } : { type, conversationId: convId };
  }

  /**
   * Switch the actively-viewed conversation. Null → no conversation open.
   * Idempotent: a no-op when convId already matches the current state.
   */
  setActive(convId: string | null): void {
    if (this.active === convId) return;
    if (this.active) {
      this.send(this.viewingFrame('viewing.stop', this.active));
    }
    this.active = convId;
    // Clear stale peers immediately — the server will deliver the fresh
    // list, but in the meantime we don't want the thread header showing
    // viewers from the previous conversation.
    this.peers = [];
    if (convId) {
      this.send(this.viewingFrame('viewing.start', convId));
    }
  }

  /**
   * Drain one `conversation.viewing.updated` payload pushed by the
   * gateway. Frames for a different conversation than the active one are
   * ignored — that can happen briefly during a switch when the previous
   * conversation's "you left" broadcast arrives after we've already moved
   * on.
   */
  handleViewingUpdated(payload: unknown): void {
    const p = payload as Partial<ViewingUpdatedPayload> | null;
    if (!p || typeof p.conversationId !== 'string' || !Array.isArray(p.viewers)) return;
    if (this.active === null || p.conversationId !== this.active) return;
    const self = this.selfId();
    const next = p.viewers.filter(
      (v): v is PresenceViewer =>
        typeof v?.userId === 'string' &&
        typeof v?.displayName === 'string' &&
        v.userId !== self,
    );
    this.peers = next;
  }

  /**
   * Re-announce the active conversation after a WS reconnect. The server
   * cleared the previous socket's viewing state on disconnect, so the new
   * socket starts blank — without this, the operator silently drops out
   * of the viewer list for everyone else.
   */
  resumeActive(): void {
    if (this.active) {
      this.send(this.viewingFrame('viewing.start', this.active));
    }
  }

  /** App teardown — best-effort viewing.stop + clear state. */
  dispose(): void {
    if (this.active) {
      this.send(this.viewingFrame('viewing.stop', this.active));
    }
    this.active = null;
    this.peers = [];
  }
}

// ---- Module singleton (mirrors lib/embed/bridge-singleton.svelte.ts) ----
//
// App.svelte constructs the PresenceTracker once realtime is up and calls
// setPresence(tracker). ConversationHeader resolves it via getPresence()
// so the badge can render without prop drilling through ThreadPage.

let _presence: PresenceTracker | null = null;

export function setPresence(p: PresenceTracker | null): void {
  _presence = p;
}

export function getPresence(): PresenceTracker | null {
  return _presence;
}
